import { DoseUnit } from "$global-providers/rxdb-provider/collections/prescription";
import {
  AdministrationApproach,
  type Quantity,
  RateUnit,
  type TimeUnit,
} from "elephant-web/gql";

const conversionRateMap = {
  hour: 1 / 24,
  day: 1,
  week: 7,
  month: 28,
};

/** @deprecated - Use (and implement) calculateDispensableQuantity below */
export const calculateAmountToDispense = ({
  rate,
  duration,
  dose,
}: {
  rate: Quantity;
  duration: Quantity;
  dose?: Quantity;
}) => {
  if (dose?.unit !== "tablet" && dose?.unit !== "capsule") {
    return undefined;
  }

  let frequencyValue = rate.value;
  const freqUnit = rate?.unit;
  let durationValue = duration?.value;
  const durationUnit = duration?.unit;
  const quantityValue = dose?.value;

  if (
    !freqUnit ||
    !durationUnit ||
    !frequencyValue ||
    !durationValue ||
    !quantityValue
  ) {
    return 0;
  }

  if (frequencyValue && durationValue && freqUnit && durationUnit) {
    frequencyValue /= conversionRateMap[freqUnit.toLowerCase()] ?? 1;
    durationValue *= conversionRateMap[durationUnit.toLowerCase()] ?? 1;
  }

  if (quantityValue) {
    return Math.ceil(
      Number(frequencyValue) * Number(durationValue) * quantityValue,
    );
  }

  return Math.ceil(Number(frequencyValue) * Number(durationValue));
};

export const calculateDispensableQuantity = ({
  howItsTaken,
  doseValue,
  doseUnit,
  rateValue,
  rateUnit,
  howLongForValue,
  howLongForUnit,
}: {
  howItsTaken: AdministrationApproach;
  doseValue: number;
  doseUnit: DoseUnit;
  rateValue: number;
  rateUnit: RateUnit;
  howLongForValue: number;
  howLongForUnit: TimeUnit;
}) => {
  if (
    !(doseUnit === DoseUnit.Capsule || doseUnit === DoseUnit.Tablet) ||
    rateUnit === RateUnit.MlHour
  )
    return 0;

  const mapAdminApproachToCalculationFn = {
    [AdministrationApproach.AsRequired]: () =>
      calculateRoutineQuantity({
        doseValue: doseValue,
        rateValue: rateValue,
        rateUnit: rateUnit as unknown as TimeUnit,
        howLongForValue: howLongForValue,
        howLongForUnit: howLongForUnit,
      }),
    [AdministrationApproach.Outpatient]: () =>
      calculateRoutineQuantity({
        doseValue: doseValue,
        rateValue: rateValue,
        rateUnit: rateUnit,
        howLongForValue: howLongForValue,
        howLongForUnit: howLongForUnit,
      }),
    [AdministrationApproach.Stat]: () => doseValue,
  };
  const calculationFn = mapAdminApproachToCalculationFn[howItsTaken];
  if (!calculationFn) return 0;
  return Math.ceil(calculationFn());
};

const calculateRoutineQuantity = ({
  doseValue,
  rateValue,
  rateUnit,
  howLongForValue,
  howLongForUnit,
}: {
  doseValue: number;
  rateValue: number;
  rateUnit: RateUnit | TimeUnit;
  howLongForValue: number;
  howLongForUnit: TimeUnit;
}) => {
  if (rateUnit === RateUnit.MlHour) return 0;
  return (
    doseValue *
    (rateValue / conversionRateMap[rateUnit.toLowerCase()]) *
    (howLongForValue * conversionRateMap[howLongForUnit.toLowerCase()])
  );
};
